import { supabase } from '../lib/supabase';

/**
 * Review Service
 * Handles review creation, retrieval, and photo uploads for completed bookings
 */

/**
 * Creates a new review for a booking
 * @param {Object} reviewData - Review data
 * @param {string} reviewData.bookingId - Booking ID
 * @param {string} reviewData.serviceType - Type of service (accommodation, restaurant, activity, transportation)
 * @param {string} reviewData.serviceName - Name of the service
 * @param {number} reviewData.rating - Rating (1-5)
 * @param {string} reviewData.title - Review title
 * @param {string} reviewData.comment - Review comment
 * @param {File[]} reviewData.photos - Array of photo files
 * @returns {Object} Result with success status and data/error
 */
export async function createReview(reviewData) {
  try {
    // Get authenticated user
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Upload photos if provided
    let photoUrls = [];
    if (reviewData?.photos && reviewData?.photos?.length > 0) {
      const uploadResults = await uploadReviewPhotos(reviewData?.photos, user?.id);
      if (uploadResults?.success) {
        photoUrls = uploadResults?.paths;
      } else {
        console.error('Photo upload failed:', uploadResults?.error);
      }
    }

    // Insert review into database
    const { data, error } = await supabase?.from('reviews')?.insert({
        user_id: user?.id,
        booking_id: reviewData?.bookingId,
        service_type: reviewData?.serviceType,
        service_name: reviewData?.serviceName,
        rating: reviewData?.rating,
        title: reviewData?.title,
        comment: reviewData?.comment,
        photos: photoUrls
      })?.select()?.single();

    if (error) {
      console.error('Review creation error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Review creation error:', error);
    return { success: false, error: error?.message || 'Failed to create review' };
  }
}

/**
 * Uploads review photos to Supabase storage
 * @param {File[]} photos - Array of photo files
 * @param {string} userId - User ID
 * @returns {Object} Result with success status and photo paths
 */
export async function uploadReviewPhotos(photos, userId) {
  try {
    const uploadPromises = photos?.map(async (photo, index) => {
      const fileExt = photo?.name?.split('.')?.pop();
      const fileName = `${userId}/${Date.now()}-${index}.${fileExt}`;
      
      const { data, error } = await supabase?.storage?.from('review-photos')?.upload(fileName, photo, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Photo upload error:', error);
        return null;
      }

      return data?.path;
    });

    const results = await Promise.all(uploadPromises);
    const successfulUploads = results?.filter(path => path !== null);

    return {
      success: successfulUploads?.length > 0,
      paths: successfulUploads,
      error: successfulUploads?.length === 0 ? 'All photo uploads failed' : null
    };
  } catch (error) {
    console.error('Photo upload error:', error);
    return { success: false, paths: [], error: error?.message };
  }
}

/**
 * Gets signed URL for a review photo
 * @param {string} photoPath - Path to photo in storage
 * @returns {string|null} Signed URL or null
 */
export async function getReviewPhotoUrl(photoPath) {
  try {
    const { data, error } = await supabase?.storage?.from('review-photos')?.createSignedUrl(photoPath, 3600); // 1 hour expiry

    if (error) {
      console.error('Error getting photo URL:', error);
      return null;
    }

    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting photo URL:', error);
    return null;
  }
}

/**
 * Gets all reviews for a specific booking
 * @param {string} bookingId - Booking ID
 * @returns {Object} Result with reviews data
 */
export async function getReviewsByBooking(bookingId) {
  try {
    const { data, error } = await supabase?.from('reviews')?.select('*')?.eq('booking_id', bookingId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching reviews:', error);
      return { success: false, error: error?.message, data: [] };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return { success: false, error: error?.message, data: [] };
  }
}

/**
 * Gets all reviews by the current user
 * @returns {Object} Result with reviews data
 */
export async function getUserReviews() {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return { success: false, error: 'User not authenticated', data: [] };
    }

    const { data, error } = await supabase?.from('reviews')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user reviews:', error);
      return { success: false, error: error?.message, data: [] };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching user reviews:', error);
    return { success: false, error: error?.message, data: [] };
  }
}

/**
 * Checks if a booking has been reviewed by the current user
 * @param {string} bookingId - Booking ID
 * @returns {boolean} True if reviewed, false otherwise
 */
export async function hasUserReviewedBooking(bookingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return false;
    }

    const { data, error } = await supabase?.from('reviews')?.select('id')?.eq('booking_id', bookingId)?.eq('user_id', user?.id)?.maybeSingle();

    if (error) {
      console.error('Error checking review status:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking review status:', error);
    return false;
  }
}

/**
 * Checks if a booking is eligible for review (completed and past checkout date)
 * @param {Object} booking - Booking object
 * @returns {boolean} True if eligible for review
 */
export function isBookingEligibleForReview(booking) {
  if (!booking) return false;
  
  const checkoutDate = new Date(booking.checkOut);
  const today = new Date();
  
  // Booking must be past checkout date and payment must be completed
  return checkoutDate < today && booking?.paymentStatus === 'paid';
}

/**
 * Updates an existing review
 * @param {string} reviewId - Review ID
 * @param {Object} updates - Fields to update
 * @returns {Object} Result with success status
 */
export async function updateReview(reviewId, updates) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    const { data, error } = await supabase?.from('reviews')?.update(updates)?.eq('id', reviewId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) {
      console.error('Review update error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Review update error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Deletes a review
 * @param {string} reviewId - Review ID
 * @returns {Object} Result with success status
 */
export async function deleteReview(reviewId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    const { error } = await supabase?.from('reviews')?.delete()?.eq('id', reviewId)?.eq('user_id', user?.id);

    if (error) {
      console.error('Review deletion error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Review deletion error:', error);
    return { success: false, error: error?.message };
  }
}